import React from 'react';
import { Check } from 'lucide-react';
import { useLanguage } from '../../components/LanguageContext';
import './portals.css';

/**
 * Owner | Tenant switch, shown at the top of BOTH sign-in screens.
 *
 * The entry used to be a list of role rows: you chose a role, then you got a
 * login. That made the role a decision you could only revise by backing out of
 * the sign-in. Here the choice lives on the sign-in itself, so someone who
 * lands as an owner and is actually a tenant switches in place.
 *
 * ORDER IS FIXED: Property Owner first, Tenant second. Owner is the account we
 * invite; a tenant arrives because their landlord put them here.
 *
 * Only the two launch roles appear (see LAUNCH_ROLES in RoleSelectScreen) —
 * this is a switch between the two finished portals, not a replacement for the
 * full role list, which is still reachable on ?roles=all.
 */
export type SwitchableRole = 'owner' | 'tenant';

interface Props {
  value: SwitchableRole;
  onChange: (role: SwitchableRole) => void;
  /** Hidden when the host screen already asks the question in its own words. */
  caption?: boolean;
}

const ORDER: { role: SwitchableRole; label: string }[] = [
  { role: 'owner', label: 'rsOwnerTitle' },
  { role: 'tenant', label: 'rsTenantTitle' },
];

const RoleSwitch: React.FC<Props> = ({ value, onChange, caption = true }) => {
  const { t } = useLanguage();

  return (
    <div className="rw-wrap">
      {caption && <p className="rw-caption">{t('rwCaption')}</p>}

      <div className="rw-switch" role="radiogroup" aria-label={t('rwCaption')}>
        {ORDER.map(({ role, label }) => {
          const on = value === role;
          return (
            <button
              key={role}
              type="button"
              role="radio"
              aria-checked={on}
              className={`rw-pill${on ? ' rw-pill--on' : ''}`}
              onClick={() => { if (!on) onChange(role); }}
            >
              <span className="rw-dot" aria-hidden="true">
                {on && <Check size={11} strokeWidth={3.2} />}
              </span>
              <span className="rw-label">{t(label)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default RoleSwitch;
